import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Sidebar, CustomText, PrimaryCta } from '../../../components';
import Core from '../../../common/clientSdk';

// Define editable metrics - key matches what OverviewSection expects
const EDITABLE_METRICS = [
    { key: 'views', label: 'Views' },
    { key: 'video_likes', label: 'Video Likes' },
    { key: 'static_likes', label: 'Static Likes' },
    { key: 'video_comments', label: 'Video Comments' },
    { key: 'static_comments', label: 'Static Comments' },
    { key: 'er_avg', label: 'E.R. (Avg.)' },
    { key: 'er_video', label: 'E.R. (Video)' },
    { key: 'er_static', label: 'E.R. (Static)' },
    // { key: 'unique_influencers', label: 'Unique Influencers' },
    { key: 'total_engagements', label: 'Total Engagements' },
    { key: 'cpe', label: 'CPE' },
    { key: 'cpv', label: 'CPV' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'reach', label: 'Reach' },
    { key: 'share', label: 'Share' },
    { key: 'saves', label: 'Saves' },
    { key: 'views_subscriber_ratio', label: 'Views/Subscriber Ratio' },
    { key: 'profile_engagements', label: 'Profile Engagements' },
    { key: 'replies', label: 'Replies' },
    { key: 'submissions', label: 'Submissions' },
    { key: 'remixes', label: 'Remixes' },
    { key: 'conversions', label: 'Conversions' },
    { key: 'sticker_taps', label: 'Sticker Taps' },
    { key: 'planned_cpv', label: 'Planned CPV' },
    { key: 'planned_views', label: 'Planned Views' },
    { key: 'installs', label: 'Installs' },
    { key: 'registration', label: 'Registration' },
    { key: 'deposit', label: 'Deposit' },
    { key: 'total_clicks', label: 'Total Clicks' },
];

const AUTO_CALCULATED_METRICS = new Set([
    // 'views',
    // 'likes',
    // 'comments',
    'er_avg',
    'er_video',
    'er_static',
    'views_subscriber_ratio',
    'profile_engagements',
    'total_engagements',
    'cpe',
    'cpv',
    'impressions',
    'reach',
    // 'share',
    // 'saves',
    // 'replies',
    // 'submissions',
    // 'remixes',
    // 'conversions',
    // 'sticker_taps',
    // 'planned_cpv',
    // 'planned_views',
    // 'installs',
    // 'registration',
    // 'deposit',
    // 'total_clicks',
]);

const CustomizeMetricsSidebar = ({
    isOpen,
    onClose,
    currentMetricValues = {},
    onSaveCustomValues,
    isLoading: parentIsLoading = false,
    reportId,
    campaignId
}) => {
    // Local state for custom values
    const [customValues, setCustomValues] = useState({});
    // Local loading state
    const [isSaving, setIsSaving] = useState(false);

    // Initialize local state from props when sidebar opens
    useEffect(() => {
        if (isOpen) {
            setCustomValues(currentMetricValues || {});
        }
    }, [isOpen, currentMetricValues]);

    // Handle custom value change
    const handleCustomValueChange = (key, value) => {
        setCustomValues(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    // Handle visibility toggle
    // Save changes
    const handleSave = () => {
        setIsSaving(true);

        if (!reportId) {
            toast.error("Report ID is missing. Cannot save.");
            setIsSaving(false);
            return;
        }

        const statisticsData = {
            views: Number(customValues.views) || 0,
            video_likes: Number(customValues.video_likes) || 0,
            static_likes: Number(customValues.static_likes) || 0,
            video_comments: Number(customValues.video_comments) || 0,
            static_comments: Number(customValues.static_comments) || 0,
            avg_eng_rate: customValues.er_avg || "0",
            video_eng_rate: customValues.er_video || "0",
            static_eng_rate: customValues.er_static || "0",
            total_eng_rate: customValues.total_engagements || "0",
            cpe: customValues.cpe || "0",
            cpv: customValues.cpv || "0",
            impressions: customValues.impressions || "0",
            reach: customValues.reach || "0",
            share: customValues.share || "0",
            saves: customValues.saves || "0",
            views_subscriber_ratio: customValues.views_subscriber_ratio || "0",
            profile_eng_rate: customValues.profile_engagements || "0",
            replies: customValues.replies || "0",
            submissions: customValues.submissions || "0",
            remixes: customValues.remixes || "0",
            conversions: customValues.conversions || "0",
            sticker_taps: customValues.sticker_taps || "0",
            planned_cpv: customValues.planned_cpv || "0",
            planned_views: customValues.planned_views || "0",
            installs: customValues.installs || "0",
            registration: customValues.registration || "0",
            deposit: customValues.deposit || "0",
            total_clicks: customValues.total_clicks || "0"
        };

        const params = {
            report_id: reportId,
            campaign_id: campaignId,
            statistics_data: JSON.stringify(statisticsData)
        };

        Core.customizeStatistics(
            (response) => {
                setIsSaving(false);
                toast.success("Statistics saved successfully");
                onSaveCustomValues?.(customValues);
            },
            (error) => {
                setIsSaving(false);
                toast.error(error || "Failed to save statistics");
            },
            (loading) => {
                setIsSaving(loading);
            },
            params
        );
    };

    const sidebarHeader = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CustomText p text="Customize Statistics" />
        </div>
    );

    // Sidebar footer
    const sidebarFooter = () => (
        <div className="manage-section-footer-buttons">
            <PrimaryCta
                text="Save Changes"
                onClick={handleSave}
                isLoading={isSaving || parentIsLoading}
                fullWidth100
                className="manage-section-footer-btn"
            />
        </div>
    );

    return (
        <Sidebar
            isOpen={isOpen}
            onClose={onClose}
            Header={sidebarHeader}
            Footer={sidebarFooter}
            sidebarWidth="450px"
            headerAlignment="left"
        >
            <div className="manage-section-sidebar-content">
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="visibility-section">
                        <p className="section-description">
                            Enter the custom metric values you want to override for the overview cards.
                        </p>
                        <div className="toggle-list">
                            {EDITABLE_METRICS.map((metric) => (
                                <div key={metric.key} className="toggle-item">
                                    <div className="toggle-item-content">
                                        <span className="toggle-label">{metric.label}</span>
                                    </div>
                                    <div className={`metric-input-with-tooltip${AUTO_CALCULATED_METRICS.has(metric.key) ? ' metric-input-with-tooltip--auto' : ''}`}>
                                        <input
                                            type="text"
                                            className="customize-metric-input"
                                            value={customValues[metric.key] || ''}
                                            placeholder={`Enter ${metric.label}`}
                                            disabled={AUTO_CALCULATED_METRICS.has(metric.key)}
                                            onChange={(e) => handleCustomValueChange(metric.key, e.target.value)}
                                        />
                                        {AUTO_CALCULATED_METRICS.has(metric.key) && (
                                            <div className="chart-tooltip">
                                                <div className="tooltip-content">
                                                    <div className="tooltip-row">
                                                        <strong>Metric calculated automatically</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Sidebar>
    );
};

export default CustomizeMetricsSidebar;
